import React, { useEffect, useMemo, useState } from "react";
import {
  financeGetPaymentDetail,
  financeGetWalletDashboard,
  financeListPayments,
  financeListRefunds,
} from "../../../api/adminFinanceApi";

const cn = (...xs) => xs.filter(Boolean).join(" ");
const safe = (x) => (x == null ? "" : String(x));
const up = (x) => safe(x).trim().toUpperCase();
const money = (x) => {
  const n = Number(x);
  return Number.isFinite(n) ? `Rs ${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "-";
};
const dt = (x) => {
  const s = safe(x);
  return s ? s.replace("T", " ").replace("+05:30", "").slice(0, 19) : "-";
};
const listOrEmpty = (value) => (Array.isArray(value) ? value : []);

function Button({ children, onClick, tone = "soft", disabled, className = "", type = "button" }) {
  const tones = {
    soft: "border-slate-200 bg-white text-slate-900 hover:bg-slate-50",
    dark: "border-slate-900 bg-slate-900 text-white hover:opacity-95",
  };
  return (
    <button
      type={type}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={cn(
        "inline-flex h-10 items-center justify-center rounded-md border px-4 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60",
        tones[tone],
        className
      )}
    >
      {children}
    </button>
  );
}

function Tag({ label, tone = "neutral" }) {
  const tones = {
    neutral: "border-slate-200 bg-white text-slate-700",
    good: "border-emerald-200 bg-emerald-50 text-emerald-700",
    warn: "border-amber-200 bg-amber-50 text-amber-800",
    bad: "border-rose-200 bg-rose-50 text-rose-700",
    ink: "border-slate-900 bg-slate-900 text-white",
  };
  return <span className={cn("inline-flex items-center rounded-md border px-2 py-1 text-[11px] font-semibold", tones[tone])}>{label}</span>;
}

function Section({ title, subtitle, right, children }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">{title}</div>
          {subtitle ? <div className="mt-1 text-[13px] font-medium text-slate-500">{subtitle}</div> : null}
        </div>
        {right}
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

function StatCard({ label, value, note }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-extrabold text-slate-900">{value}</div>
      {note ? <div className="mt-2 text-xs font-medium text-slate-500">{note}</div> : null}
    </div>
  );
}

function Drawer({ open, onClose, title, subtitle, children }) {
  return (
    <div className={cn("fixed inset-0 z-50", open ? "" : "pointer-events-none")}>
      <div className={cn("absolute inset-0 bg-black/30 transition-opacity", open ? "opacity-100" : "opacity-0")} onClick={onClose} />
      <div className={cn("absolute right-0 top-0 h-full w-full max-w-[820px] bg-white shadow-2xl transition-transform", open ? "translate-x-0" : "translate-x-full")}>
        <div className="flex h-full flex-col">
          <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
            <div>
              <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Transactions</div>
              <div className="mt-1 text-xl font-extrabold text-slate-900">{title}</div>
              {subtitle ? <div className="mt-1 text-[13px] font-medium text-slate-500">{subtitle}</div> : null}
            </div>
            <Button onClick={onClose}>Close</Button>
          </div>
          <div className="min-h-0 flex-1 overflow-auto bg-slate-50/40 p-5">{children}</div>
        </div>
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 break-words text-[13px] font-semibold text-slate-900">{value == null || value === "" ? "-" : String(value)}</div>
    </div>
  );
}

function statusTone(status) {
  const value = up(status);
  if (value.includes("PAID") || value === "COMPLETED" || value === "COLLECTED") return "good";
  if (value.includes("FAILED")) return "bad";
  if (value.includes("PENDING") || value.includes("REFUND") || value === "EXPECTED" || value === "IN_PROGRESS") return "warn";
  return "neutral";
}

function ActorTag({ row }) {
  const owner = row?.actorType === "OWNER";
  return <Tag label={owner ? "Wallet Debit" : "Vendor Payment"} tone={owner ? "warn" : "ink"} />;
}

function IdChips({ prefix, ids }) {
  const list = listOrEmpty(ids).filter((x) => x != null);
  if (!list.length) return <span className="text-sm font-medium text-slate-500">-</span>;
  return (
    <div className="flex flex-wrap gap-2">
      {list.slice(0, 4).map((id) => (
        <span key={`${prefix}-${id}`} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-700">
          {prefix} #{id}
        </span>
      ))}
      {list.length > 4 ? <span className="text-[11px] font-semibold text-slate-500">+{list.length - 4} more</span> : null}
    </div>
  );
}

export default function AdminTransactions() {
  const tabs = useMemo(() => [
    { key: "walletRecharge", label: "Wallet Recharge" },
    { key: "userPayment", label: "User Payment" },
    { key: "walletDeductions", label: "Wallet Deductions" },
    { key: "userRefunds", label: "User Refunds" },
    { key: "penaltyTransactions", label: "Penalty Transactions" },
  ], []);

  const [tab, setTab] = useState("walletRecharge");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [walletDashboard, setWalletDashboard] = useState(null);
  const [listData, setListData] = useState({ items: [], total: 0, pages: 1 });
  const [paymentDrawerOpen, setPaymentDrawerOpen] = useState(false);
  const [paymentDetail, setPaymentDetail] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [detailErr, setDetailErr] = useState("");

  const statusOptions = tab === "userPayment"
    ? ["", "CREATED", "PAID", "FAILED", "FULL_REFUND", "PARTIAL_REFUND"]
    : tab === "userRefunds"
    ? ["ALL", "EXPECTED", "IN_PROGRESS", "COMPLETED", "FAILED"]
    : [];

  useEffect(() => {
    let alive = true;
    (async () => {
      setBusy(true);
      setErr("");
      try {
        if (tab === "userPayment") {
          const result = await financeListPayments({ q, status, page, size });
          if (!alive) return;
          setWalletDashboard(null);
          setListData(result || { items: [], total: 0, pages: 1 });
          return;
        }
        if (tab === "userRefunds") {
          const result = await financeListRefunds({ q, status, page, size });
          if (!alive) return;
          setWalletDashboard(null);
          setListData(result || { items: [], total: 0, pages: 1 });
          return;
        }
        const dashboard = await financeGetWalletDashboard({ q });
        if (!alive) return;
        setWalletDashboard(dashboard || null);
        setListData({ items: [], total: 0, pages: 1 });
      } catch (e) {
        if (alive) setErr(e?.message || "Failed to load transactions");
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [page, q, size, status, tab]);

  useEffect(() => {
    setPage(0);
  }, [q, status, tab]);

  async function openPayment(paymentId) {
    if (!paymentId) return;
    setPaymentDrawerOpen(true);
    setPaymentDetail(null);
    setDetailErr("");
    setDetailBusy(true);
    try {
      setPaymentDetail(await financeGetPaymentDetail(paymentId));
    } catch (e) {
      setDetailErr(e?.message || "Failed to load payment detail");
    } finally {
      setDetailBusy(false);
    }
  }

  const walletRecharges = listOrEmpty(walletDashboard?.recharges);
  const walletDeductions = listOrEmpty(walletDashboard?.lateCancelFees).filter((row) => up(row?.actorType) === "OWNER");
  const penalties = listOrEmpty(walletDashboard?.lateCancelFees);
  const payments = listOrEmpty(listData?.items);
  const refunds = listOrEmpty(listData?.items);

  const summaryCards = useMemo(() => {
    if (tab === "walletRecharge") {
      return [
        { label: "Recharge Count", value: String(walletRecharges.length), note: "Owner wallet top-up rows" },
        { label: "Successful Amount", value: money(walletRecharges.filter((row) => up(row.status) === "PAID").reduce((sum, row) => sum + Number(row.amount || 0), 0)), note: "Paid wallet recharge amount" },
      ];
    }
    if (tab === "userPayment") {
      return [
        { label: "Payment Rows", value: String(listData?.total || payments.length), note: "User-side payment records" },
        { label: "Paid Amount", value: money(payments.reduce((sum, row) => sum + (up(row.status) === "PAID" ? Number(row.amount || 0) : 0), 0)), note: "Current page paid total" },
      ];
    }
    if (tab === "walletDeductions") {
      return [
        { label: "Wallet Debit Rows", value: String(walletDeductions.length), note: "Owner wallet deductions" },
        { label: "Deducted Amount", value: money(walletDeductions.reduce((sum, row) => sum + Number(row.amount || 0), 0)), note: "Current filtered view" },
      ];
    }
    if (tab === "userRefunds") {
      return [
        { label: "Refund Rows", value: String(listData?.total || refunds.length), note: "Booking + request refund records" },
        { label: "Refund Amount", value: money(refunds.reduce((sum, row) => sum + Number(row.refundAmount || 0), 0)), note: "Current page refundable total" },
      ];
    }
    return [
      { label: "Penalty Rows", value: String(penalties.length), note: "Owner + vendor penalty transactions" },
      { label: "Collected Amount", value: money(penalties.filter((row) => up(row.feeStatus) === "COLLECTED").reduce((sum, row) => sum + Number(row.amount || 0), 0)), note: "Collected penalty amount" },
    ];
  }, [listData?.total, payments, penalties, refunds, tab, walletDeductions, walletRecharges]);

  return (
    <div className="space-y-6">
      <div className="rounded-[28px] border border-slate-200 bg-white p-6">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <div className="text-[12px] font-bold uppercase tracking-[0.24em] text-slate-500">Admin Console</div>
            <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-900">Transactions</h1>
            <p className="mt-2 max-w-3xl text-sm font-medium leading-6 text-slate-500">
              One clean place for wallet recharge, user payment, wallet debit, refund, and penalty transaction tracking.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {summaryCards.map((card) => (
              <StatCard key={card.label} label={card.label} value={card.value} note={card.note} />
            ))}
          </div>
        </div>
      </div>

      <Section
        title="Transaction Tabs"
        subtitle="Switch by transaction type. Search is shared across the active tab."
        right={
          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by owner, user, payment, booking, request, ride or reference"
              className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-900 outline-none focus:border-slate-400 sm:w-[360px]"
            />
            {statusOptions.length ? (
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="h-11 rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900"
              >
                {statusOptions.map((item) => (
                  <option key={item || "ALL"} value={item}>
                    {item || "ALL"}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
        }
      >
        <div className="flex flex-wrap gap-2">
          {tabs.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={cn(
                "rounded-full border px-4 py-2 text-sm font-semibold transition",
                tab === item.key
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      </Section>

      {err ? <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-semibold text-rose-700">{err}</div> : null}

      <Section
        title={tabs.find((item) => item.key === tab)?.label || "Transactions"}
        subtitle={
          tab === "walletRecharge"
            ? "All owner wallet recharges."
            : tab === "userPayment"
            ? "User payments with mapped request and booking ids."
            : tab === "walletDeductions"
            ? "Owner wallet debits with owner, booking and ride context."
            : tab === "userRefunds"
            ? "Refund rows with user, payment, booking and request references."
            : "Owner wallet debit and vendor penalty payment rows in one operational ledger."
        }
      >
        {busy ? (
          <div className="py-10 text-sm font-semibold text-slate-500">Loading transactions...</div>
        ) : tab === "walletRecharge" ? (
          !walletRecharges.length ? (
            <div className="py-10 text-sm font-semibold text-slate-500">No wallet recharge rows found.</div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <div className="grid grid-cols-12 gap-4 border-b border-slate-200 bg-slate-50 px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                <div className="col-span-3">Owner</div>
                <div className="col-span-1">Amount</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-3">Razorpay refs</div>
                <div className="col-span-3">Timeline</div>
              </div>
              <div className="divide-y divide-slate-200">
                {walletRecharges.map((row) => (
                  <div key={row.topupId} className="grid grid-cols-12 gap-4 px-5 py-4">
                    <div className="col-span-3">
                      <div className="text-sm font-extrabold text-slate-900">{row.ownerName || `Owner #${row.ownerId}`}</div>
                      <div className="mt-1 text-xs font-medium text-slate-500">{row.ownerPhone || "-"} • Top-up #{row.topupId}</div>
                    </div>
                    <div className="col-span-1 text-sm font-extrabold text-slate-900">{money(row.amount)}</div>
                    <div className="col-span-2"><Tag label={row.status || "-"} tone={statusTone(row.status)} /></div>
                    <div className="col-span-3">
                      <div className="text-sm font-semibold text-slate-900">{row.razorpayOrderId || "-"}</div>
                      <div className="mt-1 text-xs font-medium text-slate-500">{row.razorpayPaymentId || "Payment id not captured yet"}</div>
                    </div>
                    <div className="col-span-3 space-y-1 text-xs font-medium text-slate-500">
                      <div>Created {dt(row.createdAt)}</div>
                      <div>Paid {dt(row.paidAt)}</div>
                      <div>Updated {dt(row.updatedAt)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        ) : tab === "userPayment" ? (
          !payments.length ? (
            <div className="py-10 text-sm font-semibold text-slate-500">No user payment rows found.</div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <div className="grid grid-cols-12 gap-4 border-b border-slate-200 bg-slate-50 px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                <div className="col-span-2">Payment</div>
                <div className="col-span-3">User</div>
                <div className="col-span-2">Mapped Requests</div>
                <div className="col-span-2">Mapped Bookings</div>
                <div className="col-span-1">Amount</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-1 text-right">Action</div>
              </div>
              <div className="divide-y divide-slate-200">
                {payments.map((row) => (
                  <div key={row.paymentId} className="grid grid-cols-12 gap-4 px-5 py-4">
                    <div className="col-span-2">
                      <div className="text-sm font-extrabold text-slate-900">Payment #{row.paymentId}</div>
                      <div className="mt-1 text-xs font-medium text-slate-500">Paid {dt(row.paidAt || row.createdAt)}</div>
                    </div>
                    <div className="col-span-3">
                      <div className="text-sm font-semibold text-slate-900">{row.userName || `User #${row.userId || "-"}`}</div>
                      <div className="mt-1 text-xs font-medium text-slate-500">{row.userPhone || "-"}{row.userEmail ? ` • ${row.userEmail}` : ""}</div>
                    </div>
                    <div className="col-span-2"><IdChips prefix="REQ" ids={row.requestIds} /></div>
                    <div className="col-span-2"><IdChips prefix="BOOK" ids={row.bookingIds} /></div>
                    <div className="col-span-1 text-sm font-extrabold text-slate-900">{money(row.amount)}</div>
                    <div className="col-span-1">
                      <Tag label={row.status || "-"} tone={statusTone(row.status)} />
                      <div className="mt-2 text-[11px] font-medium text-slate-500">{safe(row.flowStage).replaceAll("_", " ") || "-"}</div>
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button className="h-9 px-3" onClick={() => openPayment(row.paymentId)}>View</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        ) : tab === "walletDeductions" ? (
          !walletDeductions.length ? (
            <div className="py-10 text-sm font-semibold text-slate-500">No owner wallet deduction rows found.</div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <div className="grid grid-cols-12 gap-4 border-b border-slate-200 bg-slate-50 px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                <div className="col-span-3">Owner</div>
                <div className="col-span-2">Booking / Ride</div>
                <div className="col-span-2">Transaction Type</div>
                <div className="col-span-1">Amount</div>
                <div className="col-span-2">Reference</div>
                <div className="col-span-2">Timeline</div>
              </div>
              <div className="divide-y divide-slate-200">
                {walletDeductions.map((row, index) => (
                  <div key={row.reference || `${row.ownerId}-${index}`} className="grid grid-cols-12 gap-4 px-5 py-4">
                    <div className="col-span-3">
                      <div className="text-sm font-extrabold text-slate-900">{row.ownerName || `Owner #${row.ownerId}`}</div>
                      <div className="mt-1 text-xs font-medium text-slate-500">{row.ownerPhone || "-"}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-sm font-semibold text-slate-900">{row.bookingId ? `Booking #${row.bookingId}` : "-"}</div>
                      <div className="mt-1 text-xs font-medium text-slate-500">{row.rideId ? `Ride #${row.rideId}` : "Ride not linked"}</div>
                    </div>
                    <div className="col-span-2">
                      <ActorTag row={row} />
                      <div className="mt-2 text-[11px] font-medium text-slate-500">{row.collectionMode || "-"}</div>
                    </div>
                    <div className="col-span-1 text-sm font-extrabold text-slate-900">{money(row.amount)}</div>
                    <div className="col-span-2">
                      <div className="text-sm font-semibold text-slate-900">{row.reference || "-"}</div>
                      <div className="mt-1 text-xs font-medium text-slate-500">{row.note || "-"}</div>
                    </div>
                    <div className="col-span-2 space-y-1 text-xs font-medium text-slate-500">
                      <div>Event {dt(row.eventAt)}</div>
                      <div>Resolved {dt(row.resolvedAt)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        ) : tab === "userRefunds" ? (
          !refunds.length ? (
            <div className="py-10 text-sm font-semibold text-slate-500">No user refund rows found.</div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <div className="grid grid-cols-12 gap-4 border-b border-slate-200 bg-slate-50 px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                <div className="col-span-3">User</div>
                <div className="col-span-2">Payment / Entity</div>
                <div className="col-span-2">Booking / Request</div>
                <div className="col-span-2">Refund</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-2">Refund Ref</div>
              </div>
              <div className="divide-y divide-slate-200">
                {refunds.map((row) => (
                  <div key={`${row.entityType}-${row.entityId}`} className="grid grid-cols-12 gap-4 px-5 py-4">
                    <div className="col-span-3">
                      <div className="text-sm font-extrabold text-slate-900">{row.userName || `User #${row.userId || "-"}`}</div>
                      <div className="mt-1 text-xs font-medium text-slate-500">{row.userPhone || "-"}{row.userEmail ? ` • ${row.userEmail}` : ""}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-sm font-semibold text-slate-900">Payment #{row.paymentId || "-"}</div>
                      <div className="mt-1 text-xs font-medium text-slate-500">{row.entityType} #{row.entityId}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-sm font-semibold text-slate-900">{row.bookingId ? `Booking #${row.bookingId}` : "-"}</div>
                      <div className="mt-1 text-xs font-medium text-slate-500">{row.requestId ? `Request #${row.requestId}` : "No request link"}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-sm font-extrabold text-slate-900">{money(row.refundAmount)}</div>
                      <div className="mt-1 text-xs font-medium text-slate-500">Refunded {money(row.refundedAmount)} • Pending {money(row.pendingRefundAmount)}</div>
                    </div>
                    <div className="col-span-1">
                      <Tag label={row.refundStatus || "-"} tone={statusTone(row.refundStatus)} />
                    </div>
                    <div className="col-span-2">
                      <div className="text-sm font-semibold text-slate-900">{row.razorpayRefundId || "-"}</div>
                      <div className="mt-1 text-xs font-medium text-slate-500">{row.razorpayRefundStatus || `Updated ${dt(row.updatedAt)}`}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        ) : !penalties.length ? (
          <div className="py-10 text-sm font-semibold text-slate-500">No penalty transaction rows found.</div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-slate-200">
            <div className="grid grid-cols-12 gap-4 border-b border-slate-200 bg-slate-50 px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
              <div className="col-span-3">Actor</div>
              <div className="col-span-2">Transaction</div>
              <div className="col-span-2">Booking / Request / Ride</div>
              <div className="col-span-1">Amount</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-1">Reason</div>
              <div className="col-span-2">Reference</div>
            </div>
            <div className="divide-y divide-slate-200">
              {penalties.map((row, index) => (
                <div key={row.reference || `${row.actorType}-${index}`} className="grid grid-cols-12 gap-4 px-5 py-4">
                  <div className="col-span-3">
                    <div className="text-sm font-extrabold text-slate-900">
                      {up(row.actorType) === "OWNER" ? (row.ownerName || `Owner #${row.ownerId}`) : (row.vendorName || `Vendor #${row.vendorId}`)}
                    </div>
                    <div className="mt-1 text-xs font-medium text-slate-500">
                      {up(row.actorType) === "OWNER" ? (row.ownerPhone || "-") : (row.vendorPhone || "-")}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <ActorTag row={row} />
                    <div className="mt-2 text-[11px] font-medium text-slate-500">{row.collectionMode || "-"}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-sm font-semibold text-slate-900">{row.bookingId ? `Booking #${row.bookingId}` : "-"}</div>
                    <div className="mt-1 text-xs font-medium text-slate-500">
                      {row.tripRequestId ? `Request #${row.tripRequestId}` : row.rideId ? `Ride #${row.rideId}` : row.planKey || "-"}
                    </div>
                  </div>
                  <div className="col-span-1 text-sm font-extrabold text-slate-900">{money(row.amount)}</div>
                  <div className="col-span-1"><Tag label={row.feeStatus || "-"} tone={statusTone(row.feeStatus)} /></div>
                  <div className="col-span-1 text-sm font-semibold text-slate-900">{row.reasonCode || row.actorLabel || "-"}</div>
                  <div className="col-span-2">
                    <div className="text-sm font-semibold text-slate-900">{row.reference || "-"}</div>
                    <div className="mt-1 text-xs font-medium text-slate-500">{dt(row.eventAt)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "userPayment" || tab === "userRefunds" ? (
          <div className="mt-4 flex flex-col gap-3 border-t border-slate-200 pt-4 md:flex-row md:items-center md:justify-between">
            <div className="text-xs font-medium text-slate-500">{`${Number(listData?.total || 0).toLocaleString("en-IN")} total rows`}</div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <select className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold" value={size} onChange={(e) => setSize(Number(e.target.value))}>
                {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <div className="text-sm text-slate-700">Page <span className="font-semibold">{page + 1}</span> of <span className="font-semibold">{Math.max(1, listData?.pages || 1)}</span></div>
              <div className="flex gap-2">
                <Button onClick={() => setPage((current) => Math.max(0, current - 1))} disabled={page <= 0}>Prev</Button>
                <Button onClick={() => setPage((current) => current + 1)} disabled={page + 1 >= (listData?.pages || 1)}>Next</Button>
              </div>
            </div>
          </div>
        ) : null}
      </Section>

      <Drawer
        open={paymentDrawerOpen}
        onClose={() => {
          setPaymentDrawerOpen(false);
          setPaymentDetail(null);
          setDetailErr("");
        }}
        title={paymentDetail?.payment?.paymentId ? `Payment #${paymentDetail.payment.paymentId}` : "Payment"}
        subtitle="Mapped booking and request details for this payment."
      >
        {detailBusy ? (
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-8 text-sm font-semibold text-slate-600">Loading payment detail...</div>
        ) : detailErr ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-semibold text-rose-700">{detailErr}</div>
        ) : paymentDetail ? (
          <div className="space-y-4">
            <Section title="Payment" subtitle="Top-level payment identifiers and user linkage.">
              <div className="grid gap-4 md:grid-cols-3">
                <KV label="Payment Id" value={paymentDetail.payment?.paymentId} />
                <KV label="Status" value={paymentDetail.payment?.status} />
                <KV label="Amount" value={money(paymentDetail.payment?.amount)} />
                <KV label="User" value={paymentDetail.payment?.userName || `User #${paymentDetail.payment?.userId || "-"}`} />
                <KV label="Phone" value={paymentDetail.payment?.userPhone} />
                <KV label="Email" value={paymentDetail.payment?.userEmail} />
                <KV label="Razorpay Order" value={paymentDetail.payment?.razorpayOrderId} />
                <KV label="Razorpay Payment" value={paymentDetail.payment?.razorpayPaymentId} />
                <KV label="Refund Pending" value={money(paymentDetail.payment?.refundPendingAmount)} />
              </div>
            </Section>

            <Section title="Mapped Requests" subtitle="Requests captured against this payment.">
              {!listOrEmpty(paymentDetail.requests).length ? (
                <div className="text-sm font-semibold text-slate-500">No mapped requests.</div>
              ) : (
                <div className="space-y-3">
                  {paymentDetail.requests.map((row) => (
                    <div key={row.requestId} className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="text-sm font-extrabold text-slate-900">Request #{row.requestId}</div>
                        <Tag label={row.status || "-"} tone={statusTone(row.status)} />
                        <Tag label={row.paymentStatus || "-"} tone={statusTone(row.paymentStatus)} />
                      </div>
                      <div className="mt-2 text-sm font-medium text-slate-600">
                        Final booking: {row.finalBookingId ? `#${row.finalBookingId}` : "-"} • Paid {money(row.paidAmount)} • Refunded {money(row.refundedAmount)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section title="Mapped Bookings" subtitle="Bookings linked to this payment.">
              {!listOrEmpty(paymentDetail.bookings).length ? (
                <div className="text-sm font-semibold text-slate-500">No mapped bookings.</div>
              ) : (
                <div className="space-y-3">
                  {paymentDetail.bookings.map((row) => (
                    <div key={row.bookingId} className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="text-sm font-extrabold text-slate-900">Booking #{row.bookingId}</div>
                        <Tag label={row.status || "-"} tone={statusTone(row.status)} />
                        <Tag label={row.paymentStatus || "-"} tone={statusTone(row.paymentStatus)} />
                      </div>
                      <div className="mt-2 text-sm font-medium text-slate-600">
                        Ride: {row.rideId ? `#${row.rideId}` : "-"} • Owner: {row.ownerId ? `#${row.ownerId}` : "-"} • Paid {money(row.paidAmount)} • Refunded {money(row.refundedAmount)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
        ) : null}
      </Drawer>
    </div>
  );
}
